import { isEmpty, uniq } from "lodash"
import React, { useEffect, useState } from "react"
import { OverlayTrigger, Spinner, Table, Tooltip } from "react-bootstrap"
import ReactFlagsSelect from "react-flags-select"
import { useTranslation } from "react-i18next"
import Flag from "react-world-flags"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import EditIcon from "../../../assets/edit.svg"
import TrashIcon from "../../../assets/trash.svg"
import { SERVICE_TYPE } from "../../../constants/constants"
import { RootState } from "../../../store"
import { loadCountry } from "../../../store/customer.store"
import { countryNamesInEnglish, getImageSrc } from "../../../utils/commonFunctions"
import UpdateOrCreateShippingDiscount from "./UpdateOrCreateShippingDiscount"

const ShippingRatesTab = ({
    customerShippingRates,
    warehouses,
    // rateSlabs,
    customer,
    shippingServices,
    setUpdateModalWarehouse,
    variationData,
    setVariationData,
    loading,
    requestsLoader,
    selectedWarehouse,
    setSelectedWarehouse,
    setShippingServices,
    handleSubmit,
    showConfirmModal,
    setShowConfirmModal
}: {
    customerShippingRates: Record<string, any>[]
    warehouses: Record<string, any>[]
    // rateSlabs: Record<string, any>[]
    customer: Record<string, any>
    shippingServices: Record<string, any>[]
    setUpdateModalWarehouse: Function
    variationData: Record<string, any>
    setVariationData: Function
    loading: boolean
    requestsLoader: boolean
    selectedWarehouse: Record<string, any>
    setSelectedWarehouse: Function
    setShippingServices: Function
    handleSubmit: Function
    showConfirmModal: string
    setShowConfirmModal: Function
}) => {
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const { countryList } = useAppSelector((state: RootState) => state.customers)
    const [addedRateSlabs, setAddedRateSlabs] = useState([] as Record<string, any>[])
    // const [addedDiscounts, setAddedDiscounts] = useState([] as Record<string, any>[])
    const [variationUpdatedData, setVariationUpdatedData] = useState({} as Record<string, any>)
    const [rateSlabType, setRateSlabType] = useState("")

    useEffect(() => {
        if (countryList?.length === 0) {
            dispatch(loadCountry())
        }
    }, [countryList?.length])

    const createWarehousesLabels = () => {
        const customLabels = {} as Record<string, any>

        warehouses?.forEach((w) => {
            return (customLabels[w?.country] = {
                primary: w?.country
            })
        })

        return customLabels
    }

    return loading || requestsLoader ? (
        <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" />
        </div>
    ) : (
        <>
            <div className="table-responsive">
                <Table>
                    <thead className="custom-thead">
                        <tr className="border rounded">
                            <th className="col-1">Warehouse</th>
                            <th className="col-3">Shipping Co. & Service</th>
                            <th className="col-2">Destination</th>
                            <th className="col-3">Weight Ranges</th>
                            <th className="col-2">Discount/Lump-sum Rate</th>
                            <th className="col-1 px-3">Action</th>
                        </tr>
                    </thead>
                    <tbody className="text-start custom-tbody">
                        {customerShippingRates?.map((shippingRate) =>
                            !isEmpty(variationUpdatedData) &&
                            variationUpdatedData?.id === shippingRate?.id &&
                            showConfirmModal === "" ? (
                                <UpdateOrCreateShippingDiscount
                                    variationData={variationUpdatedData}
                                    setVariationData={setVariationUpdatedData}
                                    selectedWarehouse={selectedWarehouse}
                                    shippingServices={shippingServices}
                                    customer={customer}
                                    countryList={countryList}
                                    addedRateSlabs={addedRateSlabs}
                                    setAddedRateSlabs={setAddedRateSlabs}
                                    // addedDiscounts={addedDiscounts}
                                    // setAddedDiscounts={setAddedDiscounts}
                                    // rateSlabs={rateSlabs}
                                    handleSubmit={handleSubmit}
                                    setSelectedWarehouse={setSelectedWarehouse}
                                    setUpdateModalWarehouse={setUpdateModalWarehouse}
                                    setShowConfirmModal={setShowConfirmModal}
                                    rateSlabType={rateSlabType}
                                    customerShippingRates={customerShippingRates}
                                />
                            ) : (
                                <tr className="rounded" key={`${shippingRate?.id}-shipping-rate`}>
                                    <td className="col-1 d-flex align-items-center">
                                        <Flag className="w-50" code={shippingRate?.warehouse?.country} />
                                        &nbsp;&nbsp;<span>{shippingRate?.warehouse?.country}</span>
                                    </td>
                                    <td className="col-3 d-flex align-items-center">
                                        <img
                                            className="w-50"
                                            src={getImageSrc(
                                                shippingRate?.shipping_service
                                                    ?.replaceAll("_", " ")
                                                    ?.toLowerCase()
                                            )}
                                        />
                                        &nbsp;&nbsp;
                                        <span className="text-capitalize text-muted">
                                            {shippingRate?.shipping_service
                                                ?.replaceAll("_", " ")
                                                ?.toLowerCase()}
                                        </span>
                                    </td>
                                    <td className="col-2 d-flex align-items-center">
                                        {shippingRate?.countries?.map((c: string, index: number) => (
                                            <OverlayTrigger
                                                placement="bottom"
                                                delay={{ show: 250, hide: 400 }}
                                                overlay={
                                                    <Tooltip id={`${c}-tooltip`}>
                                                        {countryNamesInEnglish.of(c)}
                                                    </Tooltip>
                                                }>
                                                <span className="tr-pointer">
                                                    {c}{" "}
                                                    {index === shippingRate?.countries?.length - 1 ? "" : ", "}
                                                </span>
                                            </OverlayTrigger>
                                        ))}
                                    </td>
                                    <td className="col-3 text-center">
                                        {shippingRate?.slabs
                                            ?.sort(
                                                (a: Record<string, any>, b: Record<string, any>) => a.id - b.id
                                            )
                                            .map((s: Record<string, any>) => (
                                                <span
                                                    className="d-block mb-3"
                                                    key={
                                                        s?.id
                                                    }>{`${s?.min_weight} - ${s?.max_weight} ${s?.weight_unit}`}</span>
                                            ))}
                                    </td>
                                    <td className="col-2">
                                        {shippingRate?.slabs
                                            ?.sort(
                                                (a: Record<string, any>, b: Record<string, any>) => a.id - b.id
                                            )
                                            .map((s: Record<string, any>) => (
                                                <div className="d-block mb-3">
                                                    <strong key={`${s?.id}-discount`}>
                                                        {`${s?.discount_value}`}
                                                    </strong>
                                                    {`${s?.discount_type === SERVICE_TYPE["0"] ? ` $ ${t(`per ${s?.weight_unit}`)}` : "%"}`}
                                                </div>
                                            ))}
                                    </td>
                                    <td className="col-1 d-flex justify-content-between">
                                        <img
                                            src={EditIcon}
                                            onClick={() => {
                                                setVariationData({})
                                                setShippingServices([])
                                                setAddedRateSlabs(shippingRate?.slabs)
                                                // setAddedDiscounts(shippingRate?.slabs?.map(
                                                //     (s: Record<string, any>) => {
                                                //         return { rate_slab_id: s?.id, rate_slab_discount: s?.discount }
                                                // }))
                                                setSelectedWarehouse(
                                                    warehouses?.find(
                                                        (w) => w.id === shippingRate?.warehouse?.id
                                                    )
                                                )
                                                setUpdateModalWarehouse(shippingRate?.warehouse_id)
                                                setVariationUpdatedData(shippingRate)
                                            }}
                                            className="tr-pointer py-1 w-50"
                                        />
                                        <img
                                            src={TrashIcon}
                                            onClick={() => {
                                                setVariationData({ ...shippingRate })
                                                setShowConfirmModal("deleteShippingDiscount")
                                            }}
                                            className="tr-pointer py-1 w-50 mx-1"
                                        />
                                    </td>
                                </tr>
                            )
                        )}
                        {!isEmpty(variationData) && showConfirmModal === "" && (
                            <UpdateOrCreateShippingDiscount
                                variationData={variationData}
                                setVariationData={setVariationData}
                                selectedWarehouse={selectedWarehouse}
                                shippingServices={shippingServices}
                                customer={customer}
                                countryList={countryList}
                                addedRateSlabs={addedRateSlabs}
                                setAddedRateSlabs={setAddedRateSlabs}
                                // addedDiscounts={addedDiscounts}
                                // setAddedDiscounts={setAddedDiscounts}
                                // rateSlabs={rateSlabs}
                                handleSubmit={handleSubmit}
                                setSelectedWarehouse={setSelectedWarehouse}
                                setUpdateModalWarehouse={setUpdateModalWarehouse}
                                setShowConfirmModal={setShowConfirmModal}
                                rateSlabType={rateSlabType}
                                customerShippingRates={customerShippingRates}
                            />
                        )}
                    </tbody>
                </Table>
            </div>

            <div className="d-flex justify-content-end align-items-center">
                {/* <Form.Check
                        type="radio"
                        label={t("Discount")}
                        onChange={(e: any) => {
                            setRateSlabType(SERVICE_TYPE["1"])
                        }}
                        required={true}
                        checked={
                            rateSlabType === SERVICE_TYPE["1"]
                        }
                    />
                    <Form.Check
                        type="radio"
                        label={t("Lump-sum Rate")}
                        onChange={(e: any) => {
                            setRateSlabType(SERVICE_TYPE["0"])
                        }}
                        required={true}
                        checked={
                            rateSlabType === SERVICE_TYPE["0"]
                        }
                        className={"mx-4"}
                    /> */}
                <ReactFlagsSelect
                    className="add-variant"
                    countries={warehouses
                        ?.filter((warehouse) => warehouse.active)
                        ?.map((warehouse) => warehouse.country)}
                    selected={selectedWarehouse?.country || "AE"}
                    showSelectedLabel
                    placeholder={"+ Add Variant"}
                    customLabels={createWarehousesLabels()}
                    onSelect={(code: string) => {
                        const rateWarehouse = warehouses?.find((w) => w?.country === code)
                        setAddedRateSlabs([])
                        setVariationData({
                            ...variationData,
                            warehouse_id: rateWarehouse?.id,
                            countries: uniq([...(variationData.countries || []), customer?.country])
                        })
                        setShippingServices([])
                        setSelectedWarehouse(rateWarehouse || {})
                        setUpdateModalWarehouse(rateWarehouse?.id)
                        setVariationUpdatedData({})
                    }}
                    // disabled={rateSlabType === ""}
                />
            </div>
        </>
    )
}

export default ShippingRatesTab
